import React, { useEffect, useState, useCallback, useContext, useRef } from 'react'
import { connect, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
/* --------------------------------------------- */
import { routeVarMap } from '../route'
/* --------------------------------------------- */
import utils from '../utils/utils'
import { splitLanguage } from '../utils/localStorageService'
/* --------------------------------------------- */
import useFirebaseTrace from '../hooks/useFirebaseTrace'
/* --------------------------------------------- */
import useDetailsApi, { updateProgressInDBIfExists } from '../apis/useDetailsApi'
/* --------------------------------------------- */
import useGeneralApi from '../apis/useGeneralApi'
/* --------------------------------------------- */
import DetailsPage from '../components/pages/DetailsPage'
/* --------------------------------------------- */
import { GlobalContext } from '../context/global.context'
/* --------------------------------------------- */
import useAnalytics from './useAnalytics'
import useLoggly from './useLoggly'
/* --------------------------------------------- */
import { getRecentlyWatchedLocal } from '../utils/sessionStorageService'
/* --------------------------------------------- */
// Container component which holds details page logic.
const DetailsPageContainer = ({ match, history, appConfig, metaTheme, appMessages, catalogFeature }) => {
  const [detailsSectionData, updateDetailsSectionData] = useState({})
  const detailsSectionDataRef = useRef(detailsSectionData)
  const [seasonData, updateSeasonData] = useState([])
  const [curentSeason, updateCurentSeason] = useState(null)
  const [curentSeasonEpisodeNumber, updateCurentSeasonEpisodeNumber] = useState(3)
  const [theme, updateTheme] = useState({})
  const [baseUrl, updateBaseUrl] = useState(null)
  const [mediaId, updateMediaId] = useState((match.params.mediaId || '').split('_')[0])
  const [dataLoading, updateIsDataLoading] = useState(true)
  const [mediaInfo, updateMediaInfo] = useState({})
  const [productList, updateProductList] = useState([])
  const [isProgressAPIComplete, setProgressAPIComplete] = useState(false)
  const [isDetailsAPIComplete, setDetailsAPIComplete] = useState(false)
  const [isProductLoadingComplete, updateIsProductLoadingComplete] = useState(false)
  const [episodeProgress, setEpisodeProgress] = useState([])
  const mediaInfoRef = useRef(mediaInfo)
  /* --------------------------------------------- */
  const { appLanguage, isThisCREATE, isRecentlyWatchedCallEnded, isAuthenticated, userToken, guestLoginStatus } = useContext(GlobalContext)
  /* --------------------------------------------- */
  const { sendAnalytics } = useAnalytics()
  const { log } = useLoggly()
  /* --------------------------------------------- */
  const firebaseTrace = useFirebaseTrace()
  /* --------------------------------------------- */
  const { getSeriesDetails, getMediaInfo, getMediaProgress, getProducts, getPlaybackEpisode, getChannelPrograms } = useDetailsApi()
  /* --------------------------------------------- */
  const { getChannelInfo } = useGeneralApi()
  /* --------------------------------------------- */
  const location = useLocation()
  /* --------------------------------------------- */
  const { catalogConfig = {} } = useSelector(store => store.catalog)
  const playerFeature = useSelector(store => store.featuresReducers.playerFeature)
  const { revenueFeature } = useSelector(store => store.featuresReducers)
  const playerFeatureRef = useRef(playerFeature)
  /* --------------------------------------------- */
  const [isChannel, setIsChannel] = useState(false)
  /* --------------------------------------------- */
  useEffect(() => {
    mediaInfoRef.current = mediaInfo
  }, [mediaInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    updateBaseUrl(appConfig.baseUrl)
  }, [appConfig])
  /* --------------------------------------------- */
  useEffect(() => {
    detailsSectionDataRef.current = detailsSectionData
  }, [detailsSectionData])
  /* --------------------------------------------- */
  useEffect(() => {
    playerFeatureRef.current = playerFeature
  }, [playerFeature])
  /* --------------------------------------------- */
  /**
   * Check if valid route
   */
  useEffect(() => {
    const isValidType = Object.keys(routeVarMap.type).filter((key) => {
      return routeVarMap.type[key] === match.params.type
    }).length > 0
    if (!isValidType || !match.params.mediaId) {
      history.push(`/${match.params.pageName || ''}`)
    }
  }, [match.params.type, match.params.pageName, match.params.mediaId, history])
  /* --------------------------------------------- */
  /**
   * Base url
   */
  useEffect(() => {
    updateBaseUrl(appConfig.baseUrl)
  }, [appConfig])
  /* --------------------------------------------- */
  /**
   * Route state Details
   */
  useEffect(() => {
    if (location.state && location.state.details && Object.keys(location.state.details).length > 0) {
      const recentlyWatched = getRecentlyWatchedLocal() || []
      let latestDetails = null

      if (match.params.type === routeVarMap.type.movie) {
        latestDetails = recentlyWatched.filter((item) => item.uid === (match.params.mediaId || '').split('_')[0])[0]
        if (latestDetails) {
          setProgressAPIComplete(true)
          setDetailsAPIComplete(true)
        }
        const mediaInfo = { ...location.state.details, ...latestDetails }
        updateMediaInfo(mediaInfo)
      } else if (match.params.type === routeVarMap.type.series) {
        latestDetails = recentlyWatched.filter((item) => item.seriesUid === (match.params.mediaId || '').split('_')[0])[0]
        if (latestDetails) {
          latestDetails = {
            uid: latestDetails.uid,
            streams: latestDetails.streams,
            url: latestDetails.streams,
            trailers: latestDetails.trailers,
            seriesId: latestDetails.seriesId,
            seriesUid: latestDetails.seriesUid,
            seasonUid: latestDetails.seasonUid,
            purchaseMode: latestDetails.purchaseMode,
            episodeTitle: latestDetails.title,
            episodeNumber: latestDetails.episodeNumber,
            seasonNumber: latestDetails.seasonNumber,
            proglatestDetailss: latestDetails.proglatestDetailss,
            progress: latestDetails.progress,
            duration: latestDetails.duration,
            contentGuid: latestDetails.contentGuid,
            contentOfferId: latestDetails.contentOfferId,
            mediaGuid: latestDetails.mediaGuid,
            episodeImages: latestDetails.images || []
          }
          updateMediaId(latestDetails.uid || '')
          setProgressAPIComplete(true)
        }
        const mediaInfo = { ...location.state.details, ...latestDetails }
        updateMediaInfo(mediaInfo)
      }
    }
  }, [location.state, match.params.mediaId, match.params.type])
  /* --------------------------------------------- */
  /**
   * Movie, Preview, Match, SportingEvent Details
   */
  useEffect(() => {
    let isMounted = true
    let trace = null
    if (!(match.params.mediaId || '').split('_')[0]) return
    if (isDetailsAPIComplete) return
    if (baseUrl && [
      routeVarMap.type.movie,
      routeVarMap.type.preview,
      routeVarMap.type.match,
      routeVarMap.type.sportingevent,
      routeVarMap.type.sportingEvent
    ].indexOf(match.params.type) > -1) {
      const param = {
        language: splitLanguage(appLanguage)
      }

      let type = ''
      if (match.params.type === routeVarMap.type.movie) type = 'movie'
      else if (match.params.type === routeVarMap.type.preview) type = 'preview'
      else if (match.params.type === routeVarMap.type.match) type = 'match'
      else if (match.params.type === routeVarMap.type.sportingevent) type = 'sportingevent'
      else if (match.params.type === routeVarMap.type.sportingEvent) type = 'sportingEvent'

      trace = firebaseTrace('details_page_load', {
        contentId: (match.params.mediaId || '').split('_')[0] || '',
        contentType: type || ''
      })

      getMediaInfo(baseUrl, (match.params.mediaId || '').split('_')[0], param).then((res = {}) => {
        if (!isMounted) return
        res = res || {}
        /* --------------------------------------------- */
        updateMediaInfo(mediaInfo => ({ ...mediaInfo, ...res }))
        /* --------------------------------------------- */
        if (res.title) trace.addAttrs({ contentTitle: res.title });
        trace.stop()
        trace = null
        setDetailsAPIComplete(true)
      }).catch(() => {
        if (!isMounted) return
        trace.stop()
        trace = null
        setDetailsAPIComplete(true)
        history.push(`/${match.params.pageName || ''}`)
      })
    }
    /* --------------------------------------------- */
    return () => {
      isMounted = false
      if (trace) trace.stop()
    }
  }, [
    appLanguage,
    baseUrl,
    history,
    getMediaInfo,
    match.params.type,
    match.params.mediaId,
    isDetailsAPIComplete,
    firebaseTrace,
    match.params.pageName
  ])
  /* --------------------------------------------- */
  /**
   * Movie, Preview, Match, SportingEvent Progress
   */
  useEffect(() => {
    let isMounted = true
    if (!baseUrl) return
    if (!isRecentlyWatchedCallEnded) return
    if (!(match.params.mediaId || '').split('_')[0]) return
    if ([
      routeVarMap.type.movie,
      routeVarMap.type.preview,
      routeVarMap.type.match,
      routeVarMap.type.sportingevent,
      routeVarMap.type.sportingEvent
    ].indexOf(match.params.type) === -1) return
    if (!isAuthenticated || mediaInfo?.type === 'live') {
      setProgressAPIComplete(true)
      return
    }

    let type = ''
    // console.log('## match.params.type', match.params.type, match)
    if (match.params.type === routeVarMap.type.movie) type = 'movie'
    else if (match.params.type === routeVarMap.type.preview) type = 'preview'
    else if (match.params.type === routeVarMap.type.match) type = 'match'
    else if (match.params.type === routeVarMap.type.sportingevent) type = 'sportingevent'
    else if (match.params.type === routeVarMap.type.sportingEvent) type = 'sportingEvent'
    const playerFeature = playerFeatureRef.current || {}
    const maxResumePercent = playerFeature.maxResumePercent || 100

    const recentlyWatched = getRecentlyWatchedLocal() || []
    const isStateAvailable = (location.state && location.state.details && Object.keys(location.state.details).length > 0)
    const isProgressAvailable = recentlyWatched.filter((item) => item.uid === (match.params.mediaId || '').split('_')[0])[0]
    const query = `?uid=${(match.params.mediaId || '').split('_')[0]}&maxResumePercent=${maxResumePercent}`

    getMediaProgress(baseUrl, query).then((res = {}) => {
      if (!isMounted) return
      const { content = [] } = res
      if (content && content[0]) {
        const { progress = 0, duration = 0 } = content[0].playbackProgress || {}
        const playbackProgress = { uid: (match.params.mediaId || '').split('_')[0], progress, duration, maxResumePercent, type }
        updateProgressInDBIfExists(playbackProgress)
        if (!isProgressAvailable || !isStateAvailable) {
          updateMediaInfo(mediaInfo => ({ ...mediaInfo, ...playbackProgress, type: (mediaInfo?.type || playbackProgress?.type) }))
          setProgressAPIComplete(true)
        }
      } else {
        if (!isProgressAvailable || !isStateAvailable) setProgressAPIComplete(true)
      }
    }).catch(() => {
      if (!isMounted) return
      if (!isProgressAvailable || !isStateAvailable) setProgressAPIComplete(true)
    })

    return () => {
      isMounted = false
    }
  }, [
    isAuthenticated,
    match.params.type,
    match.params.mediaId,
    getMediaProgress,
    baseUrl,
    isRecentlyWatchedCallEnded,
    location.state,
    mediaInfo.type
  ])
  /* --------------------------------------------- */
  /**
   * Series Details
   */
  useEffect(() => {
    let isMounted = true
    let trace = null
    if (baseUrl && match.params.type === routeVarMap.type.series) {

      const param = {
        language: splitLanguage(appLanguage)
      }

      trace = firebaseTrace('details_page_load', {
        contentId: (match.params.mediaId || '').split('_')[0] || '',
        contentType: 'series'
      })

      getSeriesDetails(baseUrl, (match.params.mediaId || '').split('_')[0], param)
        .then((content = {}) => {
          if (!isMounted) return
          const { title } = content || {}
          /* --------------------------------------------- */
          updateMediaInfo(mediaInfo => ({ ...mediaInfo, ...(content || {}) }))
          trace.addAttrs({ contentTitle: title || '' });
          trace.stop()
          trace = null
          setDetailsAPIComplete(true)
        }).catch(() => {
          if (!isMounted) return
          trace.stop()
          trace = null
          setDetailsAPIComplete(true)
          history.push(`/${match.params.pageName || ''}`)
        })
    }
    return () => {
      isMounted = false
      if (trace) trace.stop()
    }
  }, [
    appLanguage, baseUrl, getSeriesDetails, match.params.mediaId, match.params.type,
    match.params.pageName, history,
    firebaseTrace
  ])
  /* --------------------------------------------- */
  /**
   * Series Progress
   */
  useEffect(() => {
    let isMounted = true
    if (!baseUrl || !userToken) return
    if (!isRecentlyWatchedCallEnded) return
    if (!(match.params.mediaId || '').split('_')[0]) return
    if (match.params.type !== routeVarMap.type.series) return
    /* --------------------------------------------- */
    const playerFeature = playerFeatureRef.current || {}
    const maxResumePercent = playerFeature.maxResumePercent || 100
    /* --------------------------------------------- */
    const recentlyWatched = getRecentlyWatchedLocal() || []
    const isStateAvailable = (location.state && location.state.details && Object.keys(location.state.details).length > 0)
    const isProgressAvailable = recentlyWatched.filter((item) => item.seriesUid === (match.params.mediaId || '').split('_')[0])[0]
    /* --------------------------------------------- */
    const param = {
      language: splitLanguage(appLanguage)
    }
    /* --------------------------------------------- */
    getPlaybackEpisode(baseUrl, (match.params.mediaId || '').split('_')[0], param).then((res = {}) => {
      if (!isMounted) return
      const details = {
        uid: res.uid,
        streams: res.streams,
        url: res.streams,
        trailers: res.trailers,
        seriesId: res.seriesId || (match.params.mediaId || '').split('_')[0],
        seriesUid: res.seriesUid || (match.params.mediaId || '').split('_')[0],
        seasonUid: res.seasonUid,
        purchaseMode: res.purchaseMode,
        episodeTitle: res.title,
        episodeNumber: res.episodeNumber,
        seasonNumber: res.seasonNumber,
        progress: res.progress,
        duration: res.duration,
        contentGuid: res.contentGuid,
        contentOfferId: res.contentOfferId,
        mediaGuid: res.mediaGuid,
        maxResumePercent,
        episodeImages: res.images || []
      }
      /* --------------------------------------------- */
      updateProgressInDBIfExists({ ...details, type: res.type })
      if (!isProgressAvailable || !isStateAvailable) {
        updateMediaInfo(mediaInfo => ({ ...mediaInfo, ...details }))
        updateMediaId(res.uid)
        setProgressAPIComplete(true)
      }
      /* --------------------------------------------- */
    }).catch(() => {
      if (!isMounted) return
      if (!isProgressAvailable || !isStateAvailable) setProgressAPIComplete(true)
    })
    /* --------------------------------------------- */
    return () => {
      isMounted = false
    }
  }, [
    baseUrl,
    match.params.mediaId,
    getPlaybackEpisode,
    match.params.type,
    appLanguage,
    isRecentlyWatchedCallEnded,
    location.state,
    userToken
  ])
  /* --------------------------------------------- */
  /**
   * Season switching
   */
  useEffect(() => {
    const queryStringObj = utils.parse(location.search) || {}
    const qSeasonNumber = Number(queryStringObj.seasonNumber || -1)
    let selectedSeasonNumber = qSeasonNumber
    if (selectedSeasonNumber === -1) {
      const stateSeasonNumber = (location.state || {}).seasonNumber || -1
      selectedSeasonNumber = stateSeasonNumber
    }
    if (selectedSeasonNumber > -1) {
      seasonData.map((season) => {
        if (season.seasonNumber === selectedSeasonNumber) {
          updateCurentSeason(season.seasonUid)
        }
        return null
      })
    } else {
      if (seasonData.length > 0 && detailsSectionData.seasonUid && match.params.type === routeVarMap.type.series) {
        updateCurentSeason(detailsSectionData.seasonUid)
      }
    }
  }, [detailsSectionData.seasonUid, location.state, location.search, seasonData, match.params.type])
  /* --------------------------------------------- */
  /**
   * Episodes Progress
   */
  useEffect(() => {
    let isMounted = true
    if (!baseUrl) return
    if (!isRecentlyWatchedCallEnded) return
    if (!(match.params.mediaId || '').split('_')[0]) return
    if (match.params.type !== routeVarMap.type.series) return
    if (!curentSeason) return
    if (dataLoading) return
    if (!isAuthenticated) return
    /* --------------------------------------------- */
    const playerFeature = playerFeatureRef.current || {}
    const maxResumePercent = playerFeature.maxResumePercent || 100
    /* --------------------------------------------- */
    const query = `?seriesUid=${(match.params.mediaId || '').split('_')[0]}&seasonUid=${curentSeason}&maxResumePercent=${maxResumePercent}`
    getMediaProgress(baseUrl, query).then((res = {}) => {
      if (!isMounted) return
      const { content = [] } = res
      if (content && content[0]) {
        const recentlyWatched = getRecentlyWatchedLocal() || []
        const latestDetails = recentlyWatched.filter((item) => item.seriesUid === (match.params.mediaId || '').split('_')[0])[0] || {}
        const updatedContent = (content || []).map((item) => {
          item = item || {}
          if (item.uid === latestDetails.uid) {
            (item.playbackProgress || {}).progress = latestDetails.progress || 0
          }
          return item
        })
        setEpisodeProgress(updatedContent)
      }
    }).catch(() => { })

    return () => {
      isMounted = false
    }
  }, [
    isAuthenticated,
    match.params.type,
    match.params.mediaId,
    curentSeason,
    getMediaProgress,
    baseUrl,
    isRecentlyWatchedCallEnded,
    dataLoading
  ])
  /* --------------------------------------------- */
  /**
   * Channel Details
   */
  useEffect(() => {
    let isMounted = true
    if (baseUrl && match.params.type === routeVarMap.type.channel) {
      updateIsDataLoading(true)
      setIsChannel(true)

      if (isThisCREATE) {
        const fromTime = new Date().getTime() - 5 * 60 * 60 * 1000 // 5 hours before from Now
        const toTime = new Date().getTime() + 5 * 60 * 60 * 1000 // 5 Hours after today
        return getChannelPrograms(baseUrl, fromTime, toTime)
          .then((res) => {
            if (!isMounted) return
            const requiredChannelObject = res.entries.filter((channel) => channel.guid === (match.params.mediaId || '').split('_')[0])[0]
            const key = Object.keys(requiredChannelObject.stations)[0] || ''
            const logoKey = Object.keys(requiredChannelObject.stations[key].thumbnails)[0] || ''
            const title = requiredChannelObject.stations[key].title
            const description = requiredChannelObject.stations[key].description
            const logo = requiredChannelObject.stations[key].thumbnails[logoKey].url || ''
            const firstContent = {
              streams: '', // Live Asset URL to be added
              title: title,
              description: description,
              logo: logo,
              images: [{
                url: logo,
                type: 'landscape',
                width: 1920,
                height: 1080
              },
              {
                url: logo,
                type: 'portrait',
                width: 1280,
                height: 1920
              },
              {
                url: logo,
                type: 'portrait',
                width: 1000,
                height: 1500
              },
              {
                url: logo,
                type: 'portrait',
                width: 800,
                height: 1200
              },
              {
                url: logo,
                type: 'portrait',
                width: 500,
                height: 750
              },
              {
                url: logo,
                type: 'portrait',
                width: 200,
                height: 300
              },
              {
                url: logo,
                type: 'landscape',
                width: 240,
                height: 135
              },
              {
                url: logo,
                type: 'landscape',
                width: 320,
                height: 180
              },
              {
                url: logo,
                type: 'landscape',
                width: 640,
                height: 360
              },
              {
                url: logo,
                type: 'landscape',
                width: 1280,
                height: 720
              },
              {
                url: logo,
                type: 'square',
                width: 1080,
                height: 1080
              },
              {
                url: logo,
                type: 'square',
                width: 640,
                height: 640
              },
              {
                url: logo,
                type: 'square',
                width: 360,
                height: 360
              },
              {
                url: logo,
                type: 'landscape',
                width: 1440,
                height: 1080
              },
              {
                url: logo,
                type: 'landscape',
                width: 700,
                height: 525
              },
              {
                url: logo,
                type: 'landscape',
                width: 300,
                height: 225
              },
              {
                url: logo,
                type: 'landscape',
                width: 1500,
                height: 570
              },
              {
                url: logo,
                type: 'landscape',
                width: 700,
                height: 226
              },
              {
                url: logo,
                type: 'landscape',
                width: 480,
                height: 182
              }
              ],
              isLivePlayer: true,
              subTitle: 'CHANNEL',
              isSportingEvent: true,
              mediaId: (match.params.mediaId || '').split('_')[0]
            }
            updateMediaInfo(mediaInfo => ({ ...mediaInfo, ...firstContent }))
            setDetailsAPIComplete(true)
            setProgressAPIComplete(true)
          }).catch(() => {
            if (!isMounted) return
            setDetailsAPIComplete(true)
            setProgressAPIComplete(true)
          })
      } else {
        const queryString = utils.stringify({
          language: splitLanguage(appLanguage)
        })
        getChannelInfo(baseUrl, (match.params.mediaId || '').split('_')[0], queryString)
          .then((res) => {
            if (!isMounted) return
            const content = res.content || []
            const firstContent = {
              ...(content[0] || {}),
              isLivePlayer: true,
              subTitle: 'CHANNEL',
              isSportingEvent: true,
              mediaId: (match.params.mediaId || '').split('_')[0]
            }
            updateMediaInfo(mediaInfo => ({ ...mediaInfo, ...firstContent }))
            setDetailsAPIComplete(true)
            setProgressAPIComplete(true)
          }).catch(() => {
            if (!isMounted) return
            setDetailsAPIComplete(true)
            setProgressAPIComplete(true)
          })
      }
    }
    return () => {
      isMounted = false
    }
  }, [
    baseUrl,
    appLanguage,
    match.params.type,
    match.params.mediaId,
    getChannelInfo,
    getChannelPrograms,
    isThisCREATE
  ])
  /* --------------------------------------------- */
  /**
   * Product Details
   */
  useEffect(() => {
    if (!baseUrl || (!userToken && !['FAILED', 'NOT_NEEDED'].includes(guestLoginStatus))) return
    let isMounted = true
    const seriesId = detailsSectionData.seriesUid
    const purchaseMode = detailsSectionData.purchaseMode
    const categoryIdArr = detailsSectionData.categoryId || []
    const categoryId = categoryIdArr.join(',')
    if (purchaseMode && purchaseMode !== 'free' && false) { // Preventing Products API since its not needed for VDT.
      getProducts(baseUrl, {
        mediaId: (match.params.mediaId || '').split('_')[0],
        categoryId,
        seriesId,
        language: splitLanguage(appLanguage)
      }).then((res) => {
        if (!isMounted) return
        updateProductList({
          products: res.products,
          purchasedProducts: res.purchasedProducts ? res.purchasedProducts : []
        })
        updateIsProductLoadingComplete(true)
      }).catch(() => {
        if (!isMounted) return
        updateIsProductLoadingComplete(true)
        updateProductList({ error: true})
      })
    }
    // if (!purchaseMode) {
      updateIsProductLoadingComplete(true)
    // }
    return () => {
      isMounted = false
    }
  }, [
    detailsSectionData.seriesUid,
    detailsSectionData.categoryId,
    detailsSectionData.purchaseMode, baseUrl, getProducts,
    match.params.mediaId, appLanguage,
    userToken,
    guestLoginStatus
  ])
  /* --------------------------------------------- */
  /**
   * Dataloading state update
   */
  useEffect(() => {
    if (isDetailsAPIComplete && isProgressAPIComplete) {
      updateIsDataLoading(false)
    }
  }, [isDetailsAPIComplete, isProgressAPIComplete])
  /* --------------------------------------------- */
  /**
   * Details MetatData Preparation
   */
  useEffect(() => {
    if (!mediaInfo) return
    if (Object.keys(mediaInfo).length === 0) return
    /* --------------------------------------------- */
    const {
      streams, title, subTitle, description, seriesId, seriesUid, type = '', episodeNumber, episodeTitle, seasonNumber, seasonUid, uid, seasons = [],
      broadcastStartTime, association, images, director = [], parentalControl, purchaseMode, isCcAvailable, contentOfferId, actor = [],
      isHdAvailable, genre = [], releaseYear, displayDuration, countries = [], availableDays, trailers, contentGuid, mediaGuid, categoryId,
      progress, duration, episodeImages = []
    } = mediaInfo
    /* --------------------------------------------- */
    let allowedMetaData = []
    if (type === 'series') allowedMetaData = catalogFeature.seriesMetadata || []
    else allowedMetaData = catalogFeature.movieMetadata || []
    /* --------------------------------------------- */
    let _title = title
    if (allowedMetaData.indexOf('title') === -1) _title = ''
    /* --------------------------------------------- */
    let _description = description
    if (allowedMetaData.indexOf('description') === -1) _description = ''
    /* --------------------------------------------- */
    let _isCcAvailable = isCcAvailable
    if (allowedMetaData.indexOf('closedCaptionAvailability') === -1) _isCcAvailable = false
    /* --------------------------------------------- */
    let _isHdAvailable = isHdAvailable
    if (allowedMetaData.indexOf('qualityIndicator') === -1) _isHdAvailable = false
    /* --------------------------------------------- */
    let _releaseYear = releaseYear
    if (allowedMetaData.indexOf('year') === -1) _releaseYear = false
    /* --------------------------------------------- */
    let _parentalControl = parentalControl
    if (allowedMetaData.indexOf('parentalRating') === -1) _parentalControl = ''
    /* --------------------------------------------- */
    let _subTitle = subTitle
    if (allowedMetaData.indexOf('subTitle') === -1) _subTitle = ''
    /* --------------------------------------------- */
    let _displayDuration = displayDuration
    if (allowedMetaData.indexOf('duration') === -1) _displayDuration = ''
    /* --------------------------------------------- */
    let { date } = mediaInfo
    let isSportingEvent = false
    if ((type === 'sportingevent' || type === 'sportingEvent') && !broadcastStartTime) date = null
    if (date) {
      if (type === 'sportingevent' || type === 'sportingEvent') {
        isSportingEvent = true
        date = moment(utils.convertUTCtoRelativeTimezone(broadcastStartTime))
        date = date.format('LT, Do MMMM, YYYY')
      } else {
        date = moment(date, 'YYYY-MM-DD').format('Do MMMM, YYYY')
      }
    }
    /* --------------------------------------------- */
    const genreArray = genre.map(item => {
      return { title: item }
    })
    /* --------------------------------------------- */
    const directorArray = director.map(item => {
      return { title: item.personName }
    })
    /* --------------------------------------------- */
    const actorArray = actor.map((item, i) => {
      return { title: item.personName }
    })
    /* --------------------------------------------- */
    const countriesArray = countries.map(item => {
      return { title: item }
    })
    /* --------------------------------------------- */
    const seasonArray = (seasons || []).map(({
      description,
      uid,
      seasonNumber,
      title,
      trailers,
      availableDays,
      director = [],
      genre = [],
      actor = [],
      countries = [],
      images,
      isCcAvailable,
      numberOfEpisodes,
      releaseYear,
      parentalControl
    }) => {
      /* --------------------------------------------- */
      let _title = title
      if (allowedMetaData.indexOf('title') === -1) _title = ''
      /* --------------------------------------------- */
      let _description = description
      if (allowedMetaData.indexOf('description') === -1) _description = ''
      /* --------------------------------------------- */
      let _numberOfEpisodes = numberOfEpisodes
      if (allowedMetaData.indexOf('episodeCount') === -1) _numberOfEpisodes = ''
      /* --------------------------------------------- */
      let _releaseYear = releaseYear
      if (allowedMetaData.indexOf('year') === -1) _releaseYear = false
      /* --------------------------------------------- */
      let _parentalControl = parentalControl
      if (allowedMetaData.indexOf('parentalRating') === -1) _parentalControl = ''
      /* --------------------------------------------- */
      let _isCcAvailable = isCcAvailable
      if (allowedMetaData.indexOf('closedCaptionAvailability') === -1) _isCcAvailable = false
      /* --------------------------------------------- */
      const genreArray = genre.map(item => {
        return { title: item }
      })
      /* --------------------------------------------- */
      const directorArray = director.map(item => {
        return { title: item.personName }
      })
      /* --------------------------------------------- */
      const actorArray = actor.map((item, i) => {
        return { title: item.personName }
      })
      /* --------------------------------------------- */
      const countriesArray = countries.map(item => {
        return { title: item }
      })
      /* --------------------------------------------- */
      const details = []
      /* --------------------------------------------- */
      if (allowedMetaData.indexOf('country') > -1) {
        details.push({
          key: 'countries',
          label: appMessages.label_details_country,
          value: countries && countriesArray.length ? countriesArray : null
        })
      }
      /* --------------------------------------------- */
      if (allowedMetaData.indexOf('availability') > -1) {
        details.push({
          key: 'availableTill',
          label: appMessages.label_details_available_for,
          value: availableDays ? [{ title: `${availableDays} ${appMessages.details_days}` }] : null
        })
      }
      /* --------------------------------------------- */
      if (allowedMetaData.indexOf('genre') > -1) {
        details.push({
          key: 'genre',
          label: appMessages.label_details_genre,
          value: genre && genreArray.length ? genreArray : null
        })
      }
      /* --------------------------------------------- */
      if (allowedMetaData.indexOf('actor') > -1) {
        details.push({
          key: 'cast',
          label: appMessages.label_details_cast,
          value: actor && actorArray.length ? actorArray : null
        })
      }
      /* --------------------------------------------- */
      if (allowedMetaData.indexOf('director') > -1) {
        details.push({
          key: 'director',
          label: appMessages.label_details_director,
          value: director && directorArray.length ? directorArray : null
        })
      }
      /* --------------------------------------------- */
      return {
        title: _title,
        seasonNumber,
        description: _description,
        seasonUid: uid,
        trailers,
        availableDays,
        images,
        isCcAvailable: _isCcAvailable,
        numberOfEpisodes: _numberOfEpisodes,
        releaseYear: _releaseYear,
        parentalControl: _parentalControl,
        details: details
      }
    }).sort((a, b) => (a.seasonNumber > b.seasonNumber) ? 1 : -1)
    updateSeasonData(seasonArray)
    /* --------------------------------------------- */
    const details = []
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('date') > -1) {
      details.push({
        key: 'date',
        label: appMessages.label_details_date,
        value: date && [{ title: date }]
      })
    }
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('association') > -1) {
      details.push({
        key: 'association',
        label: appMessages.label_details_association,
        value: association && [{ title: association }]
      })
    }
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('country') > -1) {
      details.push({
        key: 'countries',
        label: appMessages.label_details_country,
        value: countries && countriesArray.length ? countriesArray : null
      })
    }
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('availability') > -1) {
      details.push({
        key: 'availableTill',
        label: appMessages.label_details_available_for,
        value: availableDays ? [{ title: `${availableDays} ${appMessages.details_days}` }] : null
      })
    }
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('genre') > -1) {
      details.push({
        key: 'genre',
        label: appMessages.label_details_genre,
        value: genre && genreArray.length ? genreArray : null
      })
    }
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('actor') > -1) {
      details.push({
        key: 'cast',
        label: appMessages.label_details_cast,
        value: actor && actorArray.length ? actorArray : null
      })
    }
    /* --------------------------------------------- */
    if (allowedMetaData.indexOf('director') > -1) {
      details.push({
        key: 'director',
        label: appMessages.label_details_director,
        value: director && directorArray.length ? directorArray : null
      })
    }
    /* --------------------------------------------- */
    updateDetailsSectionData({
      ...mediaInfo,
      uid,
      progress,
      duration,
      url: streams,
      trailers,
      title: _title,
      subTitle: _subTitle,
      description: _description,
      seriesUid,
      seriesId,
      type,
      timeLeft: broadcastStartTime,
      images,
      isSportingEvent,
      purchaseMode,
      displayDuration: _displayDuration,
      releaseYear: _releaseYear,
      isCcAvailable: _isCcAvailable,
      isHdAvailable: _isHdAvailable,
      parentalControl: _parentalControl,
      contentGuid,
      contentOfferId,
      categoryId,
      mediaGuid,
      episodeNumber,
      episodeTitle,
      episodeImages,
      seasonNumber,
      seasonUid,
      genre,
      details,
    });
    if (!dataLoading) {
      sendAnalytics({
        type: "_content.view",
        attr: {
          content_offer_id: contentOfferId,
          content_id: mediaInfo?.contentGuid,
          item_id: contentGuid,
          content_title: title,
          content_type: type,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid
        },
      });
      log({
        type: "Debug",
        attr: {
          event: "_content.view",
          content_offer_id: contentOfferId,
          content_id: mediaInfo?.contentGuid,
          item_id: contentGuid,
          content_title: title,
          content_type: type,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid
        },
      });
    }
  }, [
    mediaInfo,
    appMessages.label_details_association,
    appMessages.label_details_available_for,
    appMessages.label_details_cast,
    appMessages.label_details_country,
    appMessages.label_details_date,
    appMessages.label_details_director,
    appMessages.label_details_genre,
    appMessages.details_days,
    sendAnalytics,
    log,
    catalogFeature,
    dataLoading,
  ]);
  /* --------------------------------------------- */
  /**
   * Theme update
   */
  useEffect(() => {
    updateTheme(catalogConfig.theme)
  }, [catalogConfig.theme])
  /* --------------------------------------------- */
  const currentSeasonChange = useCallback((uid, numberOfEpisodes) => {
    updateCurentSeason(uid)
    updateCurentSeasonEpisodeNumber(numberOfEpisodes)
  }, [])
  /* --------------------------------------------- */
  return (
    <>
      <DetailsPage
        {...{
          thisIsChannel: isChannel,
          theme,
          mediaId,
          movieSeriesId: (match.params.mediaId || "").split("_")[0],
          catalogFeature,
          appMessages,
          metaTheme,
          castConfigPlaybackFormat: playerFeature && playerFeature.castConfig && playerFeature.castConfig.playbackAssetFormat,
          previewAssetFormat: playerFeature && playerFeature.fullScreenConfig && playerFeature.fullScreenConfig.previewAssetFormat,
          detailsSectionData,
          episodeProgress,
          baseUrl,
          seasonData,
          productList,
          revenueFeature,
          curentSeason,
          isProductLoadingComplete,
          handleCurrentSeasonChange: currentSeasonChange,
          dataLoading,
          curentSeasonEpisodeNumber,
          mediaInfo
        }}
      />
    </>
  );
};
/* --------------------------------------------- */
DetailsPageContainer.propTypes = {
  /** prop from react router. */
  match: PropTypes.object,
  /** app config from contentful */
  appConfig: PropTypes.object
};
/* --------------------------------------------- */
DetailsPageContainer.defaultProps = {
  match: {},
  appConfig: {},
  metaTheme: {}
}
/* --------------------------------------------- */
const mapStateToProps = (state) => {
  return {
    appConfig: state.appConfigReducers.appConfig,
    componentTheme: state.themeConfigReducers.componentTheme,
    catalogFeature: state.featuresReducers.catalogFeature,
    metaTheme: state.themeConfigReducers.metaTheme,
    appMessages: state.appConfigReducers.appMessages
  }
}
/* --------------------------------------------- */
export default connect(mapStateToProps, null)(DetailsPageContainer);
/* --------------------------------------------- */
